import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import {GetAnalysisFile} from 'analysis-tool/AnalysisTypes';
import getOutputFilesFromStep from 'analysis-tool/data/getOutputFilesFromStep';
import DashboardLayout, {BlockSpec} from 'analysis-tool/ui/components/DashboardLayout';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepHeaderRouter, {doesStepSupportHeader} from 'analysis-tool/ui/main-view/StepHeaderRouter';
import {assert, assertAndContinue} from 'Asserts';
import ToggleExpandButton from 'ui-library/buttons/ToggleExpandButton';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import Pane from 'ui-library/pane/Pane';
import UIPlaceholder from 'ui-library/UIPlaceholder';
import * as logging from 'util/Logging';

import ExternalFilePaneContent from './ExternalFilePaneContent';

const expandedFileIdState = makeUseGlobalClientState<string | null>(null);

// Keep in sync with FILE_LIMIT in benchling/pipelines/data/models/pipeline_step_data.py
const FILE_LIMIT = 25;

/**
 * @package
 */
export const DEFAULT_BLOCK_MIN_HEIGHT = 100;

/**
 * Some file previews like html look better if they are taller than the default height because the might be
 * showing interesting content, so we can adjust their default `minHeight` here.
 *
 * Typically, this should be adjusted for any preview that doesn't render the default chip in `ExternalFilePaneContent`.
 *
 * @package
 */
export const MIMETYPE_TO_BLOCK_MIN_HEIGHT: {[mimetype: string]: number | undefined} = {
  'text/html': 448,
};

/**
 * Large images need a height limit so they don't grow taller than their container and cause overflow.
 * This is in contrast to HTML files, where we do want overflow.
 *
 * @package
 */
export const MIMETYPE_TO_BLOCK_MAX_HEIGHT: {[mimetype: string]: number | undefined} = {
  'image/jpeg': 448,
  'image/png': 448,
};

function getFileBlockMinHeight(file: GetAnalysisFile): number {
  assert(file.externalFileId != null, 'Pipeline file has not been finalized');
  return (
    (file.mimeType != null ? MIMETYPE_TO_BLOCK_MIN_HEIGHT[file.mimeType] : null) ?? DEFAULT_BLOCK_MIN_HEIGHT
  );
}

function getFileBlockMaxHeight(file: GetAnalysisFile): number | undefined {
  assert(file.externalFileId != null, 'Pipeline file has not been finalized');
  return file.mimeType != null ? MIMETYPE_TO_BLOCK_MAX_HEIGHT[file.mimeType] : undefined;
}

/**
 * Render the given step's output Files in a dashboard view
 */
export default function FileDashboardView(
  props: MainViewComponentProps<AnalysisMainViewItemType.FILE_DASHBOARD>
): JSX.Element {
  const [expandedFileId, setExpandedFileId] = expandedFileIdState.useGlobalClientState(props.clientStateId);
  const files = getOutputFilesFromStep(props.step);
  if (files.length > FILE_LIMIT) {
    logging.warn(`Expected < ${FILE_LIMIT} files to render`);
  }

  const filteredFiles = files.filter((file) => file.externalFileId != null);
  assertAndContinue(
    files.length === filteredFiles.length,
    'One or more pipeline files has not been finalized'
  );

  const blockSpecs: Array<BlockSpec> = filteredFiles.map((file) => {
    // Pipeline files have already been filtered so this assertion is just for Typescript's sake
    assert(file.externalFileId != null, 'Pipeline file has not been finalized');
    return {
      key: file.id,
      // TODO(BNCH-45878): Add box-shadow
      block: (
        <Pane
          title={file.filename}
          toolbar={
            <ToggleExpandButton
              isExpanded={file.id === expandedFileId}
              onChange={() => {
                setExpandedFileId(file.id === expandedFileId ? null : file.id);
              }}
            />
          }
        >
          <ExternalFilePaneContent file={file} />
        </Pane>
      ),
      minHeight: getFileBlockMinHeight(file),
      maxHeight: getFileBlockMaxHeight(file),
    };
  });

  return (
    <div className={css(styles.stepWrapper)}>
      {doesStepSupportHeader(props.step) && expandedFileId == null && (
        <div className={css(styles.stepHeader)}>
          <StepHeaderRouter {...props} />
        </div>
      )}
      {filteredFiles.length > 0 ? (
        <DashboardLayout blockSpecs={blockSpecs} expandedBlockKey={expandedFileId} />
      ) : (
        <UIPlaceholder infoSection="This step doesn't have any files." />
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  stepHeader: {
    // DashboardLayout has top padding. Undo it to make sure we don't have double spacing between the step
    // header and DashboardLayout.
    marginBottom: -ANALYSIS_TOOL_SPACING,
  },
  stepWrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
});
